import {
  Box,
  IconButton,
  TextField,
  Typography,
  AppBar,
  Toolbar,
  Paper,
  Divider,
  Container,
  InputAdornment,
  Collapse,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import {
  ConversationDocument,
  MessageSessionType,
  MessageThreadRole,
  TMessageThread,
} from '@/types/message';

import MuiPageWrapper from '@/@mui/MuiPageWrapper';
import { useGetConversationDetails } from '@/api/endpoints/conversation/useGetConversationDetails';
import { useMarkMessagesAsRead } from '@/api/endpoints/conversation/useMarkMessagesAsRead';
import { useSendMessageToConversation } from '@/api/endpoints/conversation/useSendMessageToConversation';
import { ArrowLeftIcon } from '@/assets/icons/ArrowLeftIcon';
import { SearchMdIcon } from '@/assets/icons/SearchMdIcon';
import { Send01Icon } from '@/assets/icons/Send01Icon';
import { XCloseIcon } from '@/assets/icons/XCloseIcon';

import MessageThreadMessageList from '@/components/message/MessageThreadMessageList';

// Styled components for better Material UI integration
const ConversationContainer = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '85vh',
  width: '100%',
  minHeight: '600px',
  overflow: 'hidden',
  borderRadius: theme.spacing(2),
}));

const ConversationHeader = styled(AppBar)(({ theme }) => ({
  position: 'static',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: 'none',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const MessageContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  paddingBottom: theme.spacing(2),
}));

const MessageInputContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const MessageInput = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(3),
    backgroundColor: theme.palette.grey[50],
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.background.paper,
    },
  },
}));

const SendButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  width: 48,
  height: 48,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabled,
    color: theme.palette.action.disabled,
  },
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.grey[50],
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const SearchInput = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    fontSize: '0.875rem',
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

interface RouteParams {
  id: string;
}

const ConversationViewPage = () => {
  const history = useHistory();
  const { id } = useParams<RouteParams>();
  const markedAsReadRef = useRef<string | null>(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const { data: conversation, isLoading } = useGetConversationDetails(
    { conversation_id: id || '' },
    !!id
  );

  const sendMessageMutation = useSendMessageToConversation({
    onSuccess: () => {
      // Success notification is handled by the hook
    },
  });

  const handleSendMessage = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const message = formData.get('message') as string;
    if (message.trim() && id) {
      sendMessageMutation.mutate({
        conversation_id: id,
        content: message,
      });
      (event.target as HTMLFormElement).reset();
    }
  };

  const handleGoBack = () => {
    history.goBack();
  };

  const handleToggleSearch = () => {
    setShowSearch(!showSearch);
    if (showSearch) {
      setSearchTerm('');
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  // Convert conversation to message thread format
  const messageThread: TMessageThread | null = conversation
    ? {
      session: {
        session_id: conversation.id,
        type: MessageSessionType.sms,
        tenant_id: -1,
        building_id: -1,
        property_location_id: -1,
        from: {
          name:
              conversation.participants?.[0]?.name ||
              conversation.messages.find(msg => msg.sender === conversation.messages[0]?.sender)
                ?.sender_name ||
              conversation.title,
          source: 'sms',
          to: conversation.messages[0]?.sender || '',
        },
        subject: conversation.title,
        updated_at: conversation.updated_at,
        created_at: conversation.created_at,
      },
      messages: conversation.messages.map(msg => {
        const documents = (msg.documents as unknown as ConversationDocument[]) || [];

        return {
          id: msg.id,
          external_id: msg.external_id,
          message: msg.body || '',
          subject: msg.subject || undefined,
          role:
              msg.sender === conversation.messages[0]?.sender
                ? MessageThreadRole.tenant
                : MessageThreadRole.property_manager,
          created_at: new Date(msg.created_at),
          updated_at: new Date(msg.created_at),
          sender_name: msg.sender_name,
          participant_sid: msg.participant_sid || undefined,
          documents,
        };
      }),
    }
    : null;

  // Filter messages based on search term
  const filteredMessageThread: TMessageThread | null =
    messageThread && searchTerm.trim()
      ? {
        ...messageThread,
        messages: messageThread.messages.filter(
          msg =>
            msg.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
              (msg.sender_name &&
                msg.sender_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
              (msg.subject && msg.subject.toLowerCase().includes(searchTerm.toLowerCase()))
        ),
      }
      : messageThread;

  const markMessagesAsReadMutation = useMarkMessagesAsRead();

  useEffect(() => {
    if (conversation?.id && id && markedAsReadRef.current !== id) {
      markedAsReadRef.current = id;
      markMessagesAsReadMutation.mutate({
        conversation_id: id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.id, id]);

  if (isLoading) {
    return (
      <MuiPageWrapper>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}
          >
            <Typography variant="h6" color="text.secondary">
              Loading conversation...
            </Typography>
          </Box>
        </Container>
      </MuiPageWrapper>
    );
  }

  if (!conversation) {
    return (
      <MuiPageWrapper>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}
          >
            <Typography variant="h6" color="error">
              Conversation not found
            </Typography>
          </Box>
        </Container>
      </MuiPageWrapper>
    );
  }

  return (
    <MuiPageWrapper>
      <Container maxWidth="xl" sx={{ py: 0 }}>
        <ConversationContainer>
          <ConversationHeader>
            <Toolbar sx={{ px: 2 }}>
              <IconButton
                edge="start"
                onClick={handleGoBack}
                sx={{ mr: 2, color: 'text.primary' }}
                aria-label="go back"
              >
                <ArrowLeftIcon />
              </IconButton>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" component="h1" sx={{ fontWeight: 600 }}>
                  {conversation.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm.trim() && filteredMessageThread
                    ? `${filteredMessageThread.messages.length} of ${conversation.messages.length} messages`
                    : `${conversation.messages.length} ${
                      conversation.messages.length === 1 ? 'message' : 'messages'
                    }`}
                </Typography>
              </Box>
              <IconButton
                onClick={handleToggleSearch}
                sx={{ color: 'text.primary' }}
                aria-label="toggle search"
              >
                <SearchMdIcon />
              </IconButton>
            </Toolbar>
          </ConversationHeader>

          <Collapse in={showSearch}>
            <SearchContainer>
              <SearchInput
                fullWidth
                size="small"
                placeholder="Search messages..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchMdIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        aria-label="clear search"
                        sx={{ p: 0.5 }}
                      >
                        <XCloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </SearchContainer>
          </Collapse>

          <MessageContainer>
            <MessageThreadMessageList messageThread={filteredMessageThread} isLoading={isLoading} />
          </MessageContainer>

          <Divider />

          <MessageInputContainer>
            <Box
              component="form"
              onSubmit={handleSendMessage}
              sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}
            >
              <MessageInput
                fullWidth
                name="message"
                placeholder="Type your message..."
                variant="outlined"
                size="small"
                disabled={sendMessageMutation.isPending}
                multiline
                maxRows={4}
                sx={{ flex: 1 }}
              />
              <SendButton
                type="submit"
                disabled={sendMessageMutation.isPending}
                aria-label="send message"
              >
                <Send01Icon />
              </SendButton>
            </Box>
          </MessageInputContainer>
        </ConversationContainer>
      </Container>
    </MuiPageWrapper>
  );
};

export default ConversationViewPage;
